import AddressSearchStatus from "./AddressSearchStatus";
import React from "react";
import {
  Combobox,
  ComboboxInput,
  ComboboxList,
  ComboboxOption,
  ComboboxPopover,
} from "@reach/combobox";
import "@reach/combobox/styles.css";
import usePlacesAutocomplete, {
  getDetails,
  getGeocode,
  getLatLng,
} from "use-places-autocomplete";
import { useGoogleMaps } from "../GoogleMapsContext";

/**
 * Google Places delivery-address autocomplete (Uganda), mirroring the
 * Lusuku_POS in-house order modal. Calls onChange with:
 *   { locationName: { name, vicinity }, latLng: { lat, lng }, locationId, address }
 * or null when cleared.
 */
const AddressAutocomplete = ({ onChange, disabled, placeholder }) => {
  const { mapsApiLoaded } = useGoogleMaps();

  const {
    value,
    ready,
    suggestions: { data, loading: placesLoading, status: suggestionStatus },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    requestOptions: {
      componentRestrictions: { country: "ug" },
      location: { lat: () => 0.347596, lng: () => 32.58252 },
      radius: 200 * 1000,
      origin: { lat: 0.3148136, lng: 32.5788721 },
      strictBounds: true,
      types: ["establishment"],
    },
    debounce: 150,
  });


  const handleSelect = async (val) => {
    setValue(val, false);
    clearSuggestions();
    try {
      const results = await getGeocode({ address: val });
      const { lat, lng } = await getLatLng(results[0]);
      const details = await getDetails({ placeId: results[0].place_id });
      onChange({
        locationName: { name: details.name, vicinity: details.vicinity },
        latLng: { lat, lng },
        locationId: results[0].place_id,
        address: val,
      });
    } catch (err) {
      // Surface nothing destructive; keep the typed text as a fallback address.
      onChange({ address: val });
    }
  };

  if (!mapsApiLoaded) {
    return <div className="small text-muted">Loading Google Maps…</div>;
  }

  return (
    <Combobox onSelect={handleSelect} className="omni-address-combobox">
      <div style={{ position: "relative" }}>
        <ComboboxInput
          className="form-control omni-control"
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            if (!e.target.value) onChange(null);
          }}
          disabled={disabled || !ready}
          placeholder={placeholder || "Search delivery address in Uganda…"}
        />
        {value && (
          <button
            type="button"
            onClick={() => {
              setValue("");
              clearSuggestions();
              onChange(null);
            }}
            style={{
              position: "absolute",
              right: 10,
              top: "50%",
              transform: "translateY(-50%)",
              background: "none",
              border: "none",
              color: "#94a3b8",
              cursor: "pointer",
              fontSize: 16,
            }}
            aria-label="Clear address"
          >
            ×
          </button>
        )}
      </div>
      <AddressSearchStatus loading={placesLoading} ready={ready} status={suggestionStatus} count={data.length} />
      <ComboboxPopover style={{ zIndex: 10001 }}>
        <ComboboxList>
          {data.map(({ place_id, structured_formatting: sf }) => (
            <ComboboxOption
              key={place_id}
              value={`${sf.main_text} ${sf.secondary_text}`}
            />
          ))}
        </ComboboxList>
      </ComboboxPopover>
    </Combobox>
  );
};

export default AddressAutocomplete;
